/* src/components/Profile.js - v6.37 - Lock Account & Admin Alerts Checkbox
   CHANGELOG (v6.36 -> v6.37): The 'Account & admin alerts' notification
   category checkbox was fully clickable/functional here despite the new
   unauthenticated /notifications unsubscribe page (NotificationPrefs.js)
   deliberately locking that same category - a user could toggle it off
   right here and it would actually persist server-side. Now disabled/
   greyed out to match, with the same enforcement now also added
   server-side in routes/users.py's update_user_full() so it can't be
   bypassed via a direct API call either.
   CHANGELOG (v6.35 -> v6.36): Real bug reported ("images don't show in
   Gallery") that took a long diagnostic pass to actually pin down - the
   image data, the API, and the <img> element were all provably correct
   (fetched fine, decoded fine, correct computed styles, correct bounding
   rect) yet nothing painted on screen, not even a plain solid debug
   background color set directly on the grid container as a test. Root
   cause: the app's wallpaper (.background-layer, position:fixed,
   z-index:0) and every other visible content section (e.g. Feed's
   .card, position:relative) are POSITIONED elements, which per the CSS
   spec always paint above any plain non-positioned (position:static,
   the default) content within the same stacking context - regardless of
   z-index value or DOM order. The gallery grid div had no `position`
   set at all, so it was static and stuck painting in an earlier
   stacking step than the fixed wallpaper, which sat on top of it and
   hid it completely even though every other diagnostic signal said it
   should be visible. Fixed with one property: position: 'relative' on
   the grid container, lifting it into the same positioned paint layer
   the wallpaper and everything else already uses. (Along the way, also
   switched the thumbnails from a CSS background:url() div to a real
   <img> tag - not the actual root cause, but a legitimate improvement
   kept anyway: accessible alt text, matches what the mobile app already
   used successfully, no background-image edge cases.)
   CHANGELOG (v6.34 -> v6.35): Added "Remember my post privacy setting"
   right under Profile Visibility per explicit request/placement - a new
   remember_post_visibility toggle (default true, routes/users.py v2.10)
   that gates whether the composer's audience selector (Feed.js v7.23)
   starts on the user's last-used choice instead of always resetting to
   Public. The actual remembering happens server-side (routes/posts.py's
   handle_posts()) whenever this is on; this toggle only controls
   whether that happens, same on/off-switch pattern as the Nexus AI
   messages checkbox right below it.
   CHANGELOG (v6.33 -> v6.34): Added a "Video Playback" settings section
   (autoplay-on-scroll, start-muted) - two new per-user toggles
   (video_autoplay/video_muted_default, routes/users.py v2.9) that
   Post.js/Feed.js read to drive feed video behavior. Note shown when
   autoplay is on but muted is off, since browsers structurally block
   non-muted autoplay - this isn't a bug in the toggle, just how every
   browser behaves, so it's surfaced rather than left as a silent
   surprise.
   CHANGELOG (v6.32 -> v6.33): Added accepted-file-types +
   recommended-dimensions helper text under the Banner/Avatar upload
   buttons in the Profile Design edit panel, since neither was
   documented anywhere in the UI. Copy-only change, no logic touched.
   CHANGELOG (v6.31 -> v6.32): v6.31's own paginated
   fetch meant liking/commenting/voting from this page updated App.js's
   global state but not profilePosts (this page's own separate copy) -
   the action worked, the UI just didn't visibly reflect it, which for a
   reaction feature reads as "it's broken". App.js's handlers now return
   the mutation's result (v13.41), so this wraps each one to also patch
   profilePosts locally with that same result - same pattern as App.js's
   own applyPostUpdate/applyPostDelete.
   CHANGELOG (v6.30 -> v6.31): Profile used to filter the
   full `posts` array (a prop from App.js) down to this user's own posts
   client-side - only worked because App.js was downloading every post in
   the database. Now that the main feed is paginated (App.js/routes/
   posts.py v2.13), Profile fetches its own paginated page via the
   extended GET /api/posts/archive?username=<name>, with infinite-scroll
   load-more wired into its <Feed> the same way App.js's main feed does.
   Known limitation: a like/comment/vote made from this page doesn't
   update in place here (the mutation still succeeds server-side) since
   profilePosts is now a separate fetch from App.js's own `posts` state,
   which is where the response-based local update happens - visiting the
   profile again picks up the change. Flagged as a follow-up rather than
   solved here.
   CHANGELOG (v6.29 -> v6.30): Accepts and forwards the new onVoteComment
   prop (App.js v13.36) into its own internal <Feed>, same as
   onAddComment already is - profile-page comments now support up/down
   voting too.
   CHANGELOG (v6.28 -> v6.29): handleProfileNavigation called
   onNavigate(username) - but the onNavigate prop is just App.js's
   setView, a bare view-name setter with no concept of "which user."
   Calling it with a username set the view state to that literal string,
   matching none of App.js's view === '...' render branches - a blank
   page. handleProfileNavigation had no other callers anywhere in this
   file before the new Friends list, so this bug was never actually
   exercised until now. Added a new
   onNavigateProfile prop (App.js v13.34 passes handleNavigateProfile -
   the real function, which fetches the target user and sets both
   viewedUser and view together) and switched to calling that instead.
   CHANGELOG (v6.27 -> v6.28): The Message button in the Friends modal
   showed even for your own row (mutual friends can't normally include
   yourself, but this is how a real duplicate-key bug was discovered - a
   user reached self-messaging through this exact button). Hidden when
   f.name === user.name, as defense-in-depth alongside the real fix:
   routes/messages.py v2.3 now rejects self-messages server-side too.
   CHANGELOG (v6.26 -> v6.27): The "Friends" stat was a static number with
   no way to see who those relationships actually are with. Made it
   clickable - opens a modal fetching GET /api/users/mutual-friends?username=X
   (routes/users.py v2.7), which computes the real intersection (accounts
   this profile follows who also follow them back) rather than just
   re-exposing the raw one-directional `friends` list the count itself is
   drawn from. Each row: avatar+name+handle (tap to visit that profile,
   reusing handleProfileNavigation) and a Message button (reuses
   onStartMessage, hidden for guests). First-pass/prototype per explicit
   request - same feature ships to mobile in the next app release
   (ProfileScreen.js), this one is live immediately since web deploys are
   real-time.
   CHANGELOG (v6.25 -> v6.26): handleSave now reads two new flags routes/
   users.py v2.6 can return alongside a successful save:
   pending_admin_approval (the requested email is a nubix.ai-style domain,
   held for admin review rather than applied) and verification_sent (a
   genuine email change was applied and needs confirming within 24h) -
   surfaces each with a distinct alert instead of silently closing the
   edit view as before, since both leave the account in a state the user
   needs to know about.
   CHANGELOG (v6.24 -> v6.25): The "DISPLAY HANDLE" field was editable
   CHANGELOG (v6.24 -> v6.25): The "DISPLAY HANDLE" field was editable
   even though nothing about it was ever meant to change post-signup -
   explicit product decision (2026-08-01) to lock it down for good, one
   less thing to confuse with the (also immutable) username/profile URL.
   Relabeled to "HANDLE", now rendered disabled/greyed-out with a short
   explanatory line, matching mobile's SettingsScreen.js v2.2. editHandle
   state itself is untouched (still prefilled from user.handle in the
   existing effect) - just the input no longer accepts changes.
   CHANGELOG (v6.23 -> v6.24): Added DISPLAY NAME and EMAIL fields to the
   Identity & Preferences edit form, backed by routes/users.py v2.5's
   newly-editable 'display_name'/'email' fields (email is uniqueness-
   checked server-side, reusing the same "Taken" error already shown for
   a taken username). The profile header's big name now renders
   profileUser.display_name if set, falling back to profileUser.name as
   before - deliberately does NOT touch 'name'/'username'/'handle', which
   are still the real identity keys used everywhere else (follow, mute,
   delete account, uploads, messaging, the /profile/<name> URL) - see
   handleFollowToggle/handleMuteUser/handleDeleteAccount/handleUpload/
   handleStartMessage below, all still keyed on user.name/profileUser.name
   exactly as before.
   CHANGELOG (v6.22 -> v6.23): Fixed the real bug behind "friends-only
   profiles are unreachable" - two parts:
   1. A blocked (403) profile fetch previously rendered NOTHING at all
      (App.js only ever handled the res.ok branch) - clicking a friends-
      only/private profile silently did nothing. Now renders a minimal
      gated view (🔒, the backend's own message, and a working Follow/
      Requested button) when profileUser.__gated is set by App.js.
   2. handleFollowToggle was moved earlier in the component (a plain
      const isn't hoisted the way hooks are, and the new gated early-
      return needed to call it) and now actually reflects toggle_friend's
      real response (added/removed/requested/request_cancelled) via a new
      followOverride state, instead of just alerting "refresh to see
      changes" and touching no state at all. See routes/users.py v2.3/v2.4
      for the backend side of this (pending friend requests for
      profile_privacy=='friends' targets, mutual on accept).
   CHANGELOG (v6.21 -> v6.22): Added an "Email Notifications" section under
   Privacy & Notifications - one checkbox per category (likes_comments,
   follows, following_posts, group_posts, admin_alerts), matching
   utils.py's DEFAULT_NOTIFICATION_PREFS/NOTIFICATION_CATEGORY_MAP exactly.
   Saved as notification_prefs (nested {category: {email, push}}) via the
   existing update-full request. Push is preserved-but-not-editable here
   (mobile-only) - notifPrefs keeps the full nested shape in state so
   saving from web never clobbers a push preference set from the app.
   CHANGELOG (v6.20 -> v6.21):
   - The "Joined" date was hardcoded to new Date().toLocaleDateString(),
     meaning it always displayed today's date for every profile regardless
     of when the account was actually created. Now reads
     profileUser.joined_at. If that field is missing or invalid (e.g. on
     legacy accounts created before this field existed), shows "Unknown"
     rather than silently showing today's date again - a wrong date should
     be visibly wrong, not disguised as a real one.
   CHANGELOG (v6.19 -> v6.20):
   - Replaced the standalone "Copy Link" button with a clickable @handle
     (the handle that was already displayed under the profile name is now
     the share affordance itself, with a 🚀 icon instead of a link icon).
   - The social_links.website link now requires login for guests (opens the
     login modal instead of opening the external link in a new tab),
     matching how Message/Follow are already gated.
   CHANGELOG (v6.18 -> v6.19):
   - Added a "Copy Link" share button (mirrors the existing post-share
     "Copy Link" pattern in Feed.js) that copies a direct, shareable URL to
     this profile. Visible to everyone viewing the profile, including guests
     and the profile owner, since profile links don't require login to
     resolve (pairs with the /profile/<name> deep-link support added in
     App.js v13.24). */
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import Feed from './Feed';
import MENU_ITEMS from '../menuConfig.json';

// Matches utils.py's DEFAULT_NOTIFICATION_PREFS/NOTIFICATION_CATEGORY_MAP
// exactly - these five categories are the only ones the backend actually
// checks (see create_notification's dispatch logic).
const DEFAULT_NOTIFICATION_PREFS = {
    likes_comments: { email: true, push: true },
    follows: { email: true, push: true },
    following_posts: { email: true, push: true },
    group_posts: { email: true, push: true },
    admin_alerts: { email: true, push: true },
};
const NOTIF_PREF_LABELS = {
    likes_comments: 'Likes & comments on your posts',
    follows: 'New followers',
    following_posts: 'Posts from people you follow',
    group_posts: 'Posts in your communities',
    admin_alerts: 'Account & admin alerts',
};

// profileUser.joined_at is an ISO timestamp string set at registration
// (auth.py). Some legacy accounts predate this field and won't have it -
// for those, show "Unknown" rather than silently defaulting to today.
const formatJoinDate = (joinedAt) => {
    if (!joinedAt) return 'Unknown';
    const date = new Date(joinedAt);
    if (isNaN(date.getTime())) return 'Unknown';
    return date.toLocaleDateString();
};

function Profile({
    user,
    targetUser,
    onUpdateUser,
    onNavigate,
    onNavigateProfile,
    onDeletePost,
    onLikePost,
    onAddComment,
    onVoteComment,
    onDeleteComment,
    onEditComment,
    onEditPost,
    onStartMessage,
    onStartAIChat,
    onLogout,
    initialParams,
    onTriggerLogin,
    onNavigatePost,
    onSearchTag
}) {
    // 1. SAFE USER RESOLUTION
    const profileUser = targetUser || user;
    const isOwnProfile = user && (!targetUser || targetUser?.name === user?.name);
    const isNexus = profileUser?.name?.toLowerCase() === 'nexus'; 

    // Tracks the result of the user's own most recent Follow/Unfollow/
    // Request click, overriding whatever the initial profileUser prop
    // said - toggle_friend's response tells us exactly which of the 4
    // outcomes happened (added/removed/requested/request_cancelled), so
    // this updates the button instantly without needing a full refetch.
    // null means "use whatever the profileUser prop says".
    const [followOverride, setFollowOverride] = useState(null);

    // ==========================================
    // ALL HOOKS MUST BE DECLARED AT THE TOP LEVEL
    // BEFORE ANY CONDITIONAL RETURNS
    // ==========================================

    // Moved above the early-return checks below (was previously much
    // further down the file) - the new __gated early return needs to
    // reference this, and a plain `const` isn't hoisted the way hooks
    // are, so it has to be defined before anything that might use it,
    // not just before other hooks.
    const handleFollowToggle = async () => {
        if (!user) { if (onTriggerLogin) onTriggerLogin(); return; }
        try {
            const res = await fetch('/api/users/friend', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ current_user: user.name, target_user: profileUser.name })
            });
            const data = await res.json();
            if (res.ok) {
                if (data.action === 'added') setFollowOverride('following');
                else if (data.action === 'removed') setFollowOverride('none');
                else if (data.action === 'requested') setFollowOverride('requested');
                else if (data.action === 'request_cancelled') setFollowOverride('none');
            }
        } catch (e) { console.error(e); }
    };
    const [activeTab, setActiveTab] = useState('posts');
    const [isEditing, setIsEditing] = useState(false);
    const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(null);
    const deepLinkProcessed = useRef(false);

    // Edit State
    const [editDisplayName, setEditDisplayName] = useState('');
    const [editEmail, setEditEmail] = useState('');
    const [editHandle, setEditHandle] = useState('');
    const [editBio, setEditBio] = useState('');
    const [editBanner, setEditBanner] = useState('');
    const [editColor, setEditColor] = useState('');
    const [editPrivacy, setEditPrivacy] = useState('public');
    const [rememberPostVisibility, setRememberPostVisibility] = useState(true);
    const [allowNexusMsgs, setAllowNexusMsgs] = useState(true);
    const [videoAutoplay, setVideoAutoplay] = useState(true);
    const [videoMuted, setVideoMuted] = useState(true);
    // Full nested shape (matches utils.py's DEFAULT_NOTIFICATION_PREFS) even
    // though this screen only presents an Email toggle per category - push
    // is mobile-only, but preserving whatever push value already exists
    // (set from the mobile app) means saving from web doesn't clobber it.
    const [notifPrefs, setNotifPrefs] = useState(DEFAULT_NOTIFICATION_PREFS);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [socials, setSocials] = useState({ website: '' });
    const [customMenu, setCustomMenu] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deletePassword, setDeletePassword] = useState('');
    const [myMutedUsers, setMyMutedUsers] = useState([]);
    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const [showReportModal, setShowReportModal] = useState(false);
    const [reportReason, setReportReason] = useState('');
    const [showFriendsModal, setShowFriendsModal] = useState(false);
    const [mutualFriends, setMutualFriends] = useState([]);
    const [loadingFriends, setLoadingFriends] = useState(false);
    const profileMenuRef = useRef(null);

    const [profilePosts, setProfilePosts] = useState([]);
    const [profilePostsHasMore, setProfilePostsHasMore] = useState(false);
    const [profilePostsLoading, setProfilePostsLoading] = useState(false);
    const PROFILE_POSTS_PAGE_SIZE = 30;

    useEffect(() => {
        if (!profileUser?.name) { setProfilePosts([]); setProfilePostsHasMore(false); return; }
        setProfilePostsLoading(true);
        const params = new URLSearchParams({ username: profileUser.name, limit: PROFILE_POSTS_PAGE_SIZE });
        if (user) params.set('current_user', user.name);
        fetch(`/api/posts/archive?${params}`)
            .then(res => res.ok ? res.json() : null)
            .then(data => {
                setProfilePosts(data ? data.posts : []);
                setProfilePostsHasMore(data ? !!data.has_more : false);
            })
            .catch(err => console.error('Profile posts fetch error', err))
            .finally(() => setProfilePostsLoading(false));
    }, [profileUser?.name, user]);

    const loadMoreProfilePosts = useCallback(async () => {
        if (profilePostsLoading || !profilePostsHasMore || profilePosts.length === 0 || !profileUser?.name) return;
        setProfilePostsLoading(true);
        try {
            const oldest = profilePosts[profilePosts.length - 1]?.id;
            const params = new URLSearchParams({ username: profileUser.name, limit: PROFILE_POSTS_PAGE_SIZE, before: oldest });
            if (user) params.set('current_user', user.name);
            const res = await fetch(`/api/posts/archive?${params}`);
            if (res.ok) {
                const data = await res.json();
                setProfilePosts(prev => [...prev, ...(data.posts || [])]);
                setProfilePostsHasMore(!!data.has_more);
            }
        } catch (e) { console.error(e); }
        setProfilePostsLoading(false);
    }, [profilePosts, profilePostsHasMore, profilePostsLoading, profileUser?.name, user]);

    const galleryImages = useMemo(() =>
        profilePosts.filter(p => p.image).map(p => ({
            id: p.id,
            url: p.image
        })), [profilePosts]);

    const applyLocalPostUpdate = useCallback((updatedPost) => {
        if (!updatedPost || !updatedPost.id) return;
        setProfilePosts(prev => prev.map(p => p.id === updatedPost.id ? updatedPost : p));
    }, []);

    const handleLocalDeletePost = useCallback(async (id) => {
        const ok = await onDeletePost(id);
        if (ok) setProfilePosts(prev => prev.filter(p => p.id !== id));
    }, [onDeletePost]);
    const handleLocalLikePost = useCallback(async (postId, voteType) => {
        applyLocalPostUpdate(await onLikePost(postId, voteType));
    }, [onLikePost, applyLocalPostUpdate]);
    const handleLocalAddComment = useCallback(async (postId, text, image) => {
        applyLocalPostUpdate(await onAddComment(postId, text, image));
    }, [onAddComment, applyLocalPostUpdate]);
    const handleLocalVoteComment = useCallback(async (postId, commentId, vote) => {
        applyLocalPostUpdate(await onVoteComment(postId, commentId, vote));
    }, [onVoteComment, applyLocalPostUpdate]);
    const handleLocalDeleteComment = useCallback(async (postId, commentId) => {
        applyLocalPostUpdate(await onDeleteComment(postId, commentId));
    }, [onDeleteComment, applyLocalPostUpdate]);
    const handleLocalEditComment = useCallback(async (postId, commentId, text) => {
        applyLocalPostUpdate(await onEditComment(postId, commentId, text));
    }, [onEditComment, applyLocalPostUpdate]);
    const handleLocalEditPost = useCallback(async (postId, newText, newVisibility) => {
        applyLocalPostUpdate(await onEditPost(postId, newText, newVisibility));
    }, [onEditPost, applyLocalPostUpdate]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
                setShowProfileMenu(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        if (initialParams && initialParams.tab && !deepLinkProcessed.current) {
            if (initialParams.tab === 'gallery' && !user) {
                setActiveTab('posts');
            } else {
                setActiveTab(initialParams.tab);
                if (initialParams.tab === 'gallery' && initialParams.photoId) {
                    const idx = galleryImages.findIndex(p => p.id === parseInt(initialParams.photoId));
                    if (idx !== -1) setSelectedPhotoIndex(idx);
                }
            }
            deepLinkProcessed.current = true;
        }
    }, [initialParams, galleryImages, user]);

    useEffect(() => {
        if (isEditing && user) {
            setEditDisplayName(user.display_name || user.name || user.username || '');
            setEditEmail(user.email || '');
            setEditHandle(user.handle || '');
            setEditBio(user.bio || '');
            setEditBanner(user.banner_url || '');
            setEditColor(user.theme_color || 'rgba(43, 43, 54, 0.60)');
            setEditPrivacy(user.profile_privacy || 'public');
            setRememberPostVisibility(user.remember_post_visibility !== false);
            setAllowNexusMsgs(user.nexus_opt_out !== true);
            setVideoAutoplay(user.video_autoplay !== false);
            setVideoMuted(user.video_muted_default !== false);
            const savedPrefs = user.notification_prefs || {};
            const mergedPrefs = {};
            Object.keys(DEFAULT_NOTIFICATION_PREFS).forEach(key => {
                mergedPrefs[key] = { ...DEFAULT_NOTIFICATION_PREFS[key], ...(savedPrefs[key] || {}) };
            });
            setNotifPrefs(mergedPrefs);
            setSocials(user.social_links || { website: '' });
            setMyMutedUsers(user.muted_users || []);
            let layout = user.menu_layout || MENU_ITEMS.map(i => i.id);
            if (!layout.includes('profile')) layout = ['profile', ...layout];
            if (!layout.includes('home')) layout = [...layout, 'home'];
            setCustomMenu(layout);
        }
    }, [isEditing, user]);

    // ==========================================
    // END OF HOOKS
    // ==========================================

    const THEME_COLORS = [
        'rgba(43, 43, 54, 0.60)', 'rgba(0, 0, 0, 0.8)', 'rgba(255, 71, 87, 0.6)',
        'rgba(255, 159, 67, 0.6)', 'rgba(255, 215, 0, 0.6)', 'rgba(46, 213, 115, 0.6)',
        'rgba(0, 206, 201, 0.6)', 'rgba(30, 144, 255, 0.6)', 'rgba(5, 196, 107, 0.6)',
        'rgba(138, 43, 226, 0.6)', 'rgba(255, 105, 180, 0.6)', 'rgba(87, 95, 207, 0.6)'
    ];

    const isFollowing = followOverride === 'following' ? true : followOverride === 'none' ? false : user && (user.friends || []).includes(profileUser?.name);
    const isRequestPending = followOverride === 'requested' ? true : followOverride === 'none' ? false : !!profileUser?.request_pending;
    const visibleTabs = user ? ['posts', 'gallery', 'about'] : ['posts', 'about'];

    // 2. STABILITY CHECK: Safe to return here because all Hooks have been processed
    if (!profileUser) {
        return (
            <div className="feed-container" style={{ textAlign: 'center', padding: '50px' }}>
                <div className="card">
                    <h3>User not found</h3>
                    <p style={{ color: '#aaa' }}>The profile you are looking for is unavailable or your session has expired.</p>
                    <button onClick={() => onNavigate('home')} style={{ background: '#2ed573', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '20px', cursor: 'pointer' }}>Return Home</button>
                </div>
            </div>
        );
    }

    // Renders instead of the normal profile when App.js's fetch got a 403
    // (profile_privacy 'private' or 'friends', requester not yet allowed
    // in) - previously this state was reachable but rendered nothing at
    // all (App.js only ever handled the res.ok case), so clicking a
    // gated user's profile just silently did nothing. Real content
    // (name/avatar/bio) is deliberately never sent by the backend for a
    // blocked profile, so this can only show the username plus a working
    // Follow/Requested button - which is exactly what's needed to let
    // someone actually send/cancel a friend request in the first place.
    if (profileUser.__gated) {
        return (
            <div className="feed-container" style={{ textAlign: 'center', padding: '50px' }}>
                <div className="card">
                    <div style={{ fontSize: '2.5rem', marginBottom: '10px' }}>🔒</div>
                    <h3>@{profileUser.username}</h3>
                    <p style={{ color: '#aaa' }}>{profileUser.error || 'This profile is private.'}</p>
                    {user && (
                        <button onClick={handleFollowToggle} disabled={isRequestPending} style={{ marginTop: '15px', background: isRequestPending ? 'transparent' : '#2ed573', border: isRequestPending ? '1px solid rgba(255,255,255,0.5)' : 'none', color: 'white', padding: '8px 20px', borderRadius: '20px', cursor: isRequestPending ? 'default' : 'pointer', fontWeight: 'bold' }}>
                            {isRequestPending ? 'Requested' : 'Follow'}
                        </button>
                    )}
                    <div>
                        <button onClick={() => onNavigate('home')} style={{ marginTop: '15px', background: 'transparent', border: 'none', color: '#74b9ff', cursor: 'pointer' }}>Return Home</button>
                    </div>
                </div>
            </div>
        );
    }

    const handleProfileNavigation = (username) => {
        if (username === profileUser?.name) return;
        onNavigateProfile(username);
    };

    const handleOpenFriendsModal = async () => {
        setShowFriendsModal(true);
        setLoadingFriends(true);
        try {
            const res = await fetch(`/api/users/mutual-friends?username=${encodeURIComponent(profileUser.name)}`);
            const data = await res.json();
            setMutualFriends(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error(e);
            setMutualFriends([]);
        } finally {
            setLoadingFriends(false);
        }
    };

    const handleFriendMessage = (username) => {
        setShowFriendsModal(false);
        onStartMessage(username);
    };

    const handleMuteUser = async () => {
        if (!user) return onTriggerLogin();
        if (!window.confirm(`Mute @${profileUser?.name}? You won't see their content anymore.`)) return;
        try {
            const res = await fetch('/api/users/mute', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ current_user: user.name, target_user: profileUser.name })
            });
            const data = await res.json();
            alert(data.message);
            window.location.reload();
        } catch(e) { console.error(e); }
    };

    const handleReportUser = async () => {
        if (!reportReason) return alert("Please enter a reason.");
        try {
            await fetch('/api/report', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    reporter: user.name,
                    target_id: `User Profile: ${profileUser?.name}`,
                    reason: reportReason
                })
            });
            setShowReportModal(false);
            setReportReason('');
            if (window.confirm("Report sent. Do you want to mute this user now?")) {
                await handleMuteUser();
            } else {
                alert("Report sent to admins.");
            }
        } catch (e) { console.error(e); }
    };

    const toggleEmailPref = (key) => {
        setNotifPrefs(prev => ({ ...prev, [key]: { ...prev[key], email: !prev[key].email } }));
    };

    const handleToggleMenuItem = (id) => {
        if (id === 'profile' || id === 'home') return;
        if (customMenu.includes(id)) {
            setCustomMenu(customMenu.filter(item => item !== id));
        } else {
            setCustomMenu([...customMenu, id]);
        }
    };

    const handleMoveItem = (index, direction) => {
        const newMenu = [...customMenu];
        if (direction === 'up' && index > 0) {
            [newMenu[index], newMenu[index - 1]] = [newMenu[index - 1], newMenu[index]];
        } else if (direction === 'down' && index < newMenu.length - 1) {
            [newMenu[index], newMenu[index + 1]] = [newMenu[index + 1], newMenu[index]];
        }
        setCustomMenu(newMenu);
    };

    const handleSave = async () => {
        if (newPassword && newPassword !== confirmPassword) return alert("Passwords do not match!");
        try {
            const res = await fetch('/api/user/update-full', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: user.id,
                    display_name: editDisplayName,
                    email: editEmail,
                    handle: editHandle,
                    bio: editBio,
                    banner_url: editBanner,
                    theme_color: editColor,
                    profile_privacy: editPrivacy,
                    remember_post_visibility: rememberPostVisibility,
                    nexus_opt_out: !allowNexusMsgs,
                    video_autoplay: videoAutoplay,
                    video_muted_default: videoMuted,
                    notification_prefs: notifPrefs,
                    social_links: socials,
                    new_password: newPassword,
                    menu_layout: customMenu
                })
            });
            const data = await res.json();
            if (res.ok) {
                onUpdateUser(data.user);
                setIsEditing(false);
                if (data.pending_admin_approval) {
                    alert("Your email change requires admin approval since it uses a nubix.ai-style address. You'll be notified once it's reviewed.");
                } else if (data.verification_sent) {
                    alert("Check your new email address for a verification link. It needs to be confirmed within 24 hours.");
                }
            }
            else { alert(data.error); }
        } catch (e) { console.error(e); }
    };

    const handleUpload = async (e, type) => {
        const file = e.target.files[0];
        if (!file) return;
        const formData = new FormData();
        formData.append('file', file);
        formData.append('username', user.name);
        try {
            const res = await fetch('/api/upload', { method: 'POST', body: formData });
            if (res.ok) {
                const data = await res.json();
                if (type === 'banner') setEditBanner(data.url);
                else if (type === 'pfp') {
                    const updateRes = await fetch('/api/user/update-full', {
                        method: 'POST', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ id: user.id, profile_pic: data.url })
                    });
                    if (updateRes.ok) {
                        const updatedUser = await updateRes.json();
                        onUpdateUser(updatedUser.user);
                    }
                }
            }
        } catch (err) { console.error(err); }
    };

    const handleCustomColor = (e) => {
        const hex = e.target.value;
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        setEditColor(`rgba(${r}, ${g}, ${b}, 0.75)`);
    };

    const handleMessageClick = () => {
        if(!user) { if(onTriggerLogin) onTriggerLogin(); return; }
        if (isNexus && onStartAIChat) {
            onStartAIChat();
        } else {
            onStartMessage(profileUser.name);
        }
    };

    const handleCopyProfileLink = async () => {
        const link = `${window.location.origin}/profile/${profileUser?.name}`;
        try { await navigator.clipboard.writeText(link); alert("Profile link copied!"); } catch (err) {}
    };

    const handleDeleteAccount = async () => {
        if (!deletePassword) return alert("Please enter your password.");
        try {
            const res = await fetch('/api/user/delete-request', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username: user.name, password: deletePassword })
            });
            const data = await res.json();
            if (res.ok) {
                alert("Account Scheduled for Deletion.\nLog in anytime within 30 days to cancel.");
                onLogout();
            } else {
                alert(data.error || "Failed.");
            }
        } catch (e) { alert("Server error."); }
    };

    const nextPhoto = (e) => { e.stopPropagation(); setSelectedPhotoIndex((prev) => (prev + 1) % galleryImages.length); };
    const prevPhoto = (e) => { e.stopPropagation(); setSelectedPhotoIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length); };

    const handleUnmute = async (target) => {
        try {
            const res = await fetch('/api/users/mute', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ current_user: user.name, target_user: target })
            });
            const data = await res.json();
            setMyMutedUsers(data.muted_list || []);
        } catch (e) { console.error(e); }
    };

    if (isEditing) {
        return (
            <div className="card" style={{ padding: 0, overflow: 'hidden', position: 'relative' }}>
                <div style={{ height: '180px', background: editBanner ? `url(${editBanner}) center/cover` : 'linear-gradient(45deg, #1e90ff, #2ed573)', position: 'relative' }}>
                    <button onClick={() => setIsEditing(false)} style={{ position: 'absolute', top: '15px', right: '15px', background: 'rgba(0,0,0,0.6)', border: 'none', color: 'white', borderRadius: '50%', width: '30px', height: '30px', cursor: 'pointer' }}>✕</button>
                    <button onClick={onLogout} style={{ position: 'absolute', top: '15px', left: '15px', background: 'rgba(255, 71, 87, 0.2)', border: '1px solid #ff4757', color: '#ff4757', padding: '5px 15px', borderRadius: '20px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8rem', backdropFilter: 'blur(4px)' }}>🛑 Logout</button>
                </div>
                <div style={{ padding: '20px' }}>
                    <h2 style={{ textAlign: 'center', marginBottom: '20px', letterSpacing: '2px', textTransform: 'uppercase' }}>Profile Design</h2>
                    <div style={{ marginBottom: '30px', background: 'rgba(255,255,255,0.05)', padding: '15px', borderRadius: '10px' }}>
                        <h4 style={{ textAlign: 'center', margin: '0 0 15px 0', color: '#74b9ff', textTransform: 'uppercase', fontSize: '0.9rem' }}>Customize Navigation</h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {customMenu.map((id, index) => {
                                const item = MENU_ITEMS.find(i => i.id === id);
                                if (!item) return null;
                                const isMandatory = id === 'profile' || id === 'home';
                                return (
                                    <div key={id} style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(0,0,0,0.2)', padding: '8px', borderRadius: '5px' }}>
                                        <input type="checkbox" checked={true} disabled={isMandatory} onChange={() => handleToggleMenuItem(id)} style={{ cursor: isMandatory ? 'not-allowed' : 'pointer', opacity: 1 }} />
                                        <span style={{ fontSize: '1.2rem' }}>{item.icon}</span>
                                        <span style={{ flex: 1, fontWeight: 'bold' }}>{item.label} {isMandatory && <span style={{fontSize:'0.7rem', color:'#777', fontWeight:'normal'}}>(Fixed)</span>}</span>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                            <button onClick={() => handleMoveItem(index, 'up')} disabled={index === 0} style={{ border: 'none', background: 'none', color: index === 0 ? '#555' : 'white', cursor: 'pointer', fontSize: '0.7rem' }}>▲</button>
                                            <button onClick={() => handleMoveItem(index, 'down')} disabled={index === customMenu.length - 1} style={{ border: 'none', background: 'none', color: index === customMenu.length - 1 ? '#555' : 'white', cursor: 'pointer', fontSize: '0.7rem' }}>▼</button>
                                        </div>
                                    </div>
                                );
                            })}
                            {MENU_ITEMS.filter(i => !customMenu.includes(i.id)).map(item => (
                                <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(255,255,255,0.05)', padding: '8px', borderRadius: '5px', opacity: 0.6 }}>
                                    <input type="checkbox" checked={false} onChange={() => handleToggleMenuItem(item.id)} style={{ cursor: 'pointer' }} />
                                    <span style={{ fontSize: '1.2rem' }}>{item.icon}</span>
                                    <span style={{ flex: 1 }}>{item.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div style={{ marginBottom: '20px', display:'flex', gap:'20px', justifyContent:'center', flexWrap:'wrap' }}>
                        <div style={{ textAlign: 'center' }}>
                            <label style={{ display: 'block', color: '#a0a0b0', fontSize: '0.8rem', marginBottom: '10px' }}>BANNER</label>
                            <label style={{ background: '#333', padding: '8px 15px', borderRadius: '5px', cursor: 'pointer', fontSize: '0.9rem', display:'block', marginBottom:'5px' }}>Change Banner <input type="file" accept="image/jpeg,image/png,image/gif,image/bmp,image/tiff" hidden onChange={(e) => handleUpload(e, 'banner')} /></label>
                            <button onClick={() => setEditBanner('')} style={{ background: '#ff4757', border: 'none', color: 'white', padding: '5px 10px', borderRadius: '5px', cursor: 'pointer', fontSize:'0.8rem' }}>Remove</button>
                            <div style={{ fontSize: '0.7rem', color: '#777', marginTop: '6px', maxWidth: '160px' }}>JPG, PNG, GIF, BMP, TIFF · recommended 820×312px · up to 50MB</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <label style={{ display: 'block', color: '#a0a0b0', fontSize: '0.8rem', marginBottom: '10px' }}>AVATAR</label>
                            <label style={{ background: '#333', padding: '8px 15px', borderRadius: '5px', cursor: 'pointer', fontSize: '0.9rem', display:'block' }}>Change Photo <input type="file" accept="image/jpeg,image/png,image/gif,image/bmp,image/tiff" hidden onChange={(e) => handleUpload(e, 'pfp')} /></label>
                            <div style={{ fontSize: '0.7rem', color: '#777', marginTop: '6px', maxWidth: '160px' }}>JPG, PNG, GIF, BMP, TIFF · recommended 320×320px · up to 50MB</div>
                        </div>
                    </div>
                    <div style={{ marginBottom: '30px', textAlign: 'center' }}>
                        <label style={{ display: 'block', color: '#a0a0b0', fontSize: '0.8rem', marginBottom: '10px' }}>CARD BACKGROUND COLOR</label>
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            {THEME_COLORS.map(c => (
                                <div key={c} onClick={() => setEditColor(c)} style={{ width: '35px', height: '35px', borderRadius: '50%', background: c, border: editColor === c ? '2px solid white' : '1px solid rgba(255,255,255,0.2)', cursor: 'pointer', boxShadow: '0 2px 5px rgba(0,0,0,0.3)' }} />
                            ))}
                            <label style={{ width: '35px', height: '35px', borderRadius: '50%', background: 'linear-gradient(135deg, red, orange, yellow, green, blue, purple)', border: '1px solid rgba(255,255,255,0.2)', cursor: 'pointer', boxShadow: '0 2px 5px rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Custom Color">
                                <input type="color" style={{ opacity: 0, width: '100%', height: '100%', cursor: 'pointer' }} onChange={handleCustomColor} />
                                <span style={{ fontSize: '10px', color: 'white', textShadow: '0 0 2px black', pointerEvents: 'none' }}>+</span>
                            </label>
                        </div>
                    </div>
                    <hr style={{ borderColor: 'rgba(255,255,255,0.1)', margin: '20px 0' }} />
                    <h3 style={{ textAlign: 'center', marginBottom: '20px', letterSpacing: '2px', textTransform: 'uppercase' }}>Identity & Preferences</h3>
                    <div style={{ background: 'rgba(0,0,0,0.2)', padding:'15px', borderRadius:'10px', marginBottom: '20px' }}>
                        <label style={{ display: 'block', color: '#74b9ff', fontSize: '0.9rem', marginBottom: '10px', textTransform:'uppercase' }}>Blocked / Muted Users</label>
                        {myMutedUsers.length === 0 && <div style={{ color: '#aaa', fontSize: '0.9rem', fontStyle: 'italic' }}>You have not muted anyone.</div>}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {myMutedUsers.map(mUser => (
                                <div key={mUser} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255,255,255,0.05)', padding: '8px 12px', borderRadius: '5px' }}>
                                    <span style={{ fontWeight: 'bold' }}>@{mUser}</span>
                                    <button onClick={() => handleUnmute(mUser)} style={{ background: 'transparent', border: '1px solid #777', color: '#ccc', padding: '2px 10px', borderRadius: '15px', cursor: 'pointer', fontSize: '0.8rem' }}>Unmute</button>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        <div>
                            <label style={{ display: 'block', color: '#a0a0b0', fontSize: '0.8rem', marginBottom: '5px' }}>DISPLAY NAME</label>
                            <input className="post-input" value={editDisplayName} onChange={(e) => setEditDisplayName(e.target.value)} placeholder="Your name" />
                            <p style={{ fontSize: '0.75rem', color: '#888', marginTop: '5px' }}>Shown at the top of your profile. Your @handle and profile link stay the same.</p>
                        </div>
                        <div>
                            <label style={{ display: 'block', color: '#a0a0b0', fontSize: '0.8rem', marginBottom: '5px' }}>EMAIL</label>
                            <input className="post-input" type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} placeholder="you@example.com" />
                        </div>
                        <div>
                            <label style={{ display: 'block', color: '#a0a0b0', fontSize: '0.8rem', marginBottom: '5px' }}>HANDLE</label>
                            <input className="post-input" value={editHandle} disabled style={{ opacity: 0.5, cursor: 'not-allowed' }} />
                            <p style={{ fontSize: '0.75rem', color: '#888', marginTop: '5px' }}>Your handle is permanent and can't be changed.</p>
                        </div>
                        <div>
                            <label style={{ display: 'block', color: '#a0a0b0', fontSize: '0.8rem', marginBottom: '5px' }}>BIO</label>
                            <textarea className="post-input" value={editBio} onChange={(e) => setEditBio(e.target.value)} placeholder="Tell us about yourself..." style={{ minHeight: '80px' }} />
                        </div>
                        <div style={{ background: 'rgba(0,0,0,0.2)', padding:'15px', borderRadius:'10px' }}>
                            <label style={{ display: 'block', color: '#74b9ff', fontSize: '0.9rem', marginBottom: '10px', textTransform:'uppercase' }}>Privacy & Notifications</label>
                            <div style={{marginBottom:'10px'}}>
                                <label style={{ display: 'block', color: '#a0a0b0', fontSize: '0.8rem', marginBottom: '5px' }}>PROFILE VISIBILITY</label>
                                <select className="post-input" value={editPrivacy} onChange={(e) => setEditPrivacy(e.target.value)} style={{ width: '100%', background: '#333', color: 'white' }}>
                                    <option value="public">🌍 Public (Everyone)</option>
                                    <option value="friends">👥 Friends Only</option>
                                    <option value="private">🔒 Private (Only Me)</option>
                                </select>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                                <input type="checkbox" checked={rememberPostVisibility} onChange={(e) => setRememberPostVisibility(e.target.checked)} style={{ width: '20px', height: '20px', cursor:'pointer' }} />
                                <div>
                                    <div style={{ fontWeight: 'bold' }}>Remember my post privacy setting</div>
                                    <div style={{ fontSize: '0.8rem', color: '#aaa' }}>Your next post's audience (Public/Friends/Only Me) starts on whatever you picked last time, instead of always starting on Public.</div>
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <input type="checkbox" checked={allowNexusMsgs} onChange={(e) => setAllowNexusMsgs(e.target.checked)} style={{ width: '20px', height: '20px', cursor:'pointer' }} />
                                <div>
                                    <div style={{ fontWeight: 'bold' }}>Receive messages from Nexus AI</div>
                                    <div style={{ fontSize: '0.8rem', color: '#aaa' }}>Allow the system AI to send you tips and greetings.</div>
                                </div>
                            </div>

                            <div style={{ marginTop: '20px' }}>
                                <label style={{ display: 'block', color: '#a0a0b0', fontSize: '0.8rem', marginBottom: '10px' }}>VIDEO PLAYBACK</label>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <input type="checkbox" checked={videoAutoplay} onChange={(e) => setVideoAutoplay(e.target.checked)} style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                                        <span style={{ fontSize: '0.9rem' }}>Autoplay videos as they scroll into view</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <input type="checkbox" checked={videoMuted} onChange={(e) => setVideoMuted(e.target.checked)} style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                                        <span style={{ fontSize: '0.9rem' }}>Start videos muted</span>
                                    </div>
                                    {videoAutoplay && !videoMuted && (
                                        <div style={{ fontSize: '0.75rem', color: '#888' }}>Note: browsers block autoplay with sound - autoplaying videos will still start muted, tap to unmute.</div>
                                    )}
                                </div>
                            </div>

                            <div style={{ marginTop: '20px' }}>
                                <label style={{ display: 'block', color: '#a0a0b0', fontSize: '0.8rem', marginBottom: '10px' }}>EMAIL NOTIFICATIONS</label>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    {Object.keys(DEFAULT_NOTIFICATION_PREFS).map(key => {
                                        const isLocked = key === 'admin_alerts';
                                        return (
                                        <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '10px', opacity: isLocked ? 0.5 : 1 }}>
                                            <input
                                                type="checkbox"
                                                checked={isLocked ? true : (notifPrefs[key]?.email ?? true)}
                                                disabled={isLocked}
                                                onChange={() => !isLocked && toggleEmailPref(key)}
                                                style={{ width: '18px', height: '18px', cursor: isLocked ? 'default' : 'pointer' }}
                                            />
                                            <span style={{ fontSize: '0.9rem' }}>{NOTIF_PREF_LABELS[key]}</span>
                                            {isLocked && <span style={{ fontSize: '0.75rem', color: '#888' }}>(required)</span>}
                                        </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                        <div>
                            <label style={{ display: 'block', color: '#a0a0b0', fontSize: '0.8rem', marginBottom: '5px' }}>WEBSITE</label>
                            <input className="post-input" value={socials.website} onChange={(e) => setSocials({...socials, website: e.target.value})} placeholder="https://" />
                        </div>
                        <div style={{ borderTop:'1px solid rgba(255,255,255,0.1)', paddingTop:'15px', marginTop:'10px' }}>
                            <label style={{ display: 'block', color: '#ff4757', fontSize: '0.8rem', marginBottom: '10px' }}>CHANGE PASSWORD (OPTIONAL)</label>
                            <input className="post-input" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="New Password" style={{marginBottom:'10px'}} />
                            <input className="post-input" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm New Password" />
                        </div>
                    </div>
                    <div style={{ marginTop: '30px', textAlign: 'right' }}>
                        <button onClick={handleSave} style={{ background: '#2ed573', color: 'white', border: 'none', padding: '10px 30px', borderRadius: '30px', fontWeight: 'bold', cursor: 'pointer', fontSize: '1rem' }}>Save Profile</button>
                    </div>
                    <div style={{ marginTop: '40px', paddingTop: '20px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                        <h4 style={{ color: '#ff4757', margin: '0 0 10px 0' }}>Danger Zone</h4>
                        <button onClick={() => setShowDeleteModal(true)} style={{ background: 'rgba(255, 71, 87, 0.1)', border: '1px solid #ff4757', color: '#ff4757', padding: '10px', width: '100%', borderRadius: '5px', cursor: 'pointer' }}>Delete Account</button>
                    </div>
                </div>
                {showDeleteModal && (
                    <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.9)', zIndex: 3000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <div className="card" style={{ width: '90%', maxWidth: '400px', padding: '20px', border: '1px solid #ff4757' }}>
                            <h3 style={{ color: '#ff4757', marginTop: 0 }}>Confirm Deletion</h3>
                            <p style={{ fontSize: '0.9rem', color: '#ccc' }}>This will schedule your account for permanent deletion in 30 days. You can cancel this by logging back in before then.</p>
                            <input type="password" placeholder="Password" value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)} style={{ width: '100%', padding: '10px', marginBottom: '20px', borderRadius: '5px', border: 'none', background: 'rgba(255,255,255,0.1)', color: 'white' }} />
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button onClick={() => { setShowDeleteModal(false); setDeletePassword(''); }} style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid #aaa', color: '#aaa', borderRadius: '5px', cursor: 'pointer' }}>Cancel</button>
                                <button onClick={handleDeleteAccount} style={{ flex: 1, padding: '10px', background: '#ff4757', border: 'none', color: 'white', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}>Schedule Deletion</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    const themeStyle = {
        background: profileUser?.theme_color || 'rgba(43, 43, 54, 0.60)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.05)'
    };

    return (
        <div className="feed-container">
            <div className="card" style={{ padding: 0, overflow: 'visible', position: 'relative', marginTop: 0, ...themeStyle }}>
                {profileUser?.scheduled_deletion && (
                    <div style={{ background: '#ff4757', color: 'white', textAlign: 'center', padding: '10px', fontWeight: 'bold', fontSize: '0.9rem', borderRadius: '15px 15px 0 0' }}>
                        ⚠️ This user scheduled to have their profile deleted.
                    </div>
                )}
                <div style={{ height: '200px', background: profileUser?.banner_url ? `url(${profileUser.banner_url}) center/cover` : 'linear-gradient(45deg, #1e90ff, #2ed573)', borderRadius: profileUser?.scheduled_deletion ? '0' : '15px 15px 0 0', position: 'relative' }}>
                    {isOwnProfile && (
                        <button onClick={() => setIsEditing(true)} style={{ position: 'absolute', top: '15px', right: '15px', background: 'rgba(0,0,0,0.6)', color: 'white', border: 'none', padding: '8px 15px', borderRadius: '20px', cursor: 'pointer', backdropFilter: 'blur(4px)' }}>✎ Edit</button>
                    )}
                    {!isOwnProfile && user && (
                        <div ref={profileMenuRef} style={{ position: 'absolute', top: '15px', right: '15px', zIndex: 100 }}>
                            <button onClick={() => setShowProfileMenu(!showProfileMenu)} style={{ background: 'rgba(0,0,0,0.6)', color: 'white', border: 'none', width: '30px', height: '30px', borderRadius: '50%', cursor: 'pointer', fontWeight: 'bold', fontSize: '18px', display:'flex', alignItems:'center', justifyContent:'center' }}>⋮</button>
                            {showProfileMenu && (
                                <div style={{ position: 'absolute', right: 0, top: '35px', background: '#2f3542', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '5px', width: '140px', overflow: 'hidden', boxShadow: '0 5px 15px rgba(0,0,0,0.5)' }}>
                                    <button onClick={() => { setShowReportModal(true); setShowProfileMenu(false); }} style={{ display: 'block', width: '100%', padding: '10px', textAlign: 'left', background: 'transparent', border: 'none', color: '#ff4757', cursor: 'pointer', fontSize:'0.9rem' }}>🚩 Report User</button>
                                    <button onClick={() => { handleMuteUser(); setShowProfileMenu(false); }} style={{ display: 'block', width: '100%', padding: '10px', textAlign: 'left', background: 'transparent', border: 'none', color: '#ccc', cursor: 'pointer', fontSize:'0.9rem' }}>🔇 Mute User</button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                {showReportModal && (
                    <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.8)', zIndex: 2000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <div className="card" style={{ width: '90%', maxWidth: '400px', border: '1px solid #ff4757' }}>
                            <h3 style={{ marginTop: 0, color: '#ff4757' }}>Report User</h3>
                            <p style={{ fontSize: '0.9rem', color: '#ccc' }}>Why are you reporting {profileUser?.name}?</p>
                            <textarea value={reportReason} onChange={(e) => setReportReason(e.target.value)} placeholder="Reason..." style={{ width: '100%', height: '80px', padding: '10px', borderRadius: '5px', border: 'none', background: 'rgba(255,255,255,0.1)', color: 'white', marginBottom: '15px' }} />
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button onClick={() => setShowReportModal(false)} style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid #aaa', color: '#aaa', borderRadius: '5px', cursor: 'pointer' }}>Cancel</button>
                                <button onClick={handleReportUser} style={{ flex: 1, padding: '10px', background: '#ff4757', border: 'none', color: 'white', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}>Submit Report</button>
                            </div>
                        </div>
                    </div>
                )}
                {showFriendsModal && (
                    <div onClick={() => setShowFriendsModal(false)} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.8)', zIndex: 2000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <div className="card" onClick={(e) => e.stopPropagation()} style={{ width: '90%', maxWidth: '400px', maxHeight: '70vh', display: 'flex', flexDirection: 'column' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                <h3 style={{ margin: 0 }}>Friends</h3>
                                <button onClick={() => setShowFriendsModal(false)} style={{ background: 'none', border: 'none', color: '#aaa', fontSize: '20px', cursor: 'pointer' }}>✕</button>
                            </div>
                            <div style={{ overflowY: 'auto', flex: 1 }}>
                                {loadingFriends && <div style={{ color: '#aaa', textAlign: 'center', padding: '20px' }}>Loading...</div>}
                                {!loadingFriends && mutualFriends.length === 0 && <div style={{ color: '#aaa', textAlign: 'center', padding: '20px' }}>No mutual friends yet.</div>}
                                {!loadingFriends && mutualFriends.map(f => (
                                    <div key={f.name} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                        {f.profile_pic ? (
                                            <img src={f.profile_pic} alt={f.name} style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }} />
                                        ) : (
                                            <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: f.avatarColor || '#555', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>{f.name[0].toUpperCase()}</div>
                                        )}
                                        <div onClick={() => { setShowFriendsModal(false); handleProfileNavigation(f.name); }} style={{ flex: 1, cursor: 'pointer' }}>
                                            <div style={{ fontWeight: 'bold' }}>{f.name}</div>
                                            <div style={{ fontSize: '0.8rem', color: '#aaa' }}>{f.handle}</div>
                                        </div>
                                        {user && f.name !== user.name && (
                                            <button onClick={() => handleFriendMessage(f.name)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', padding: '6px 14px', borderRadius: '15px', cursor: 'pointer', fontSize: '0.8rem' }}>Message</button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
                <div className="profile-info" style={{ position: 'relative' }}>
                    <div className="profile-avatar" style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', top: '-60px', borderRadius: '50%', background: '#111', padding: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 10px 20px rgba(0,0,0,0.3)' }}>
                        {profileUser?.profile_pic ? (
                            <img src={profileUser.profile_pic} alt="Profile" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                        ) : (
                            <div style={{ width: '100%', height: '100%', borderRadius: '50%', background: profileUser?.avatarColor || '#333', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '50px', fontWeight: 'bold', color: 'white' }}>{profileUser?.name ? profileUser.name[0].toUpperCase() : '?'}</div>
                        )}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', paddingTop: '10px', minHeight: '60px' }}>
                        <div style={{ textAlign: 'left', maxWidth: '35%' }}>
                            <h2 style={{ margin: 0, fontSize: '1.8rem', lineHeight: '1.1', wordBreak: 'break-word' }}>{profileUser?.display_name || profileUser?.name}</h2>
                            <span
                                onClick={handleCopyProfileLink}
                                title="Copy profile link"
                                style={{ color: '#2ed573', fontWeight: 'bold', fontSize: '0.9rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '5px' }}
                            >
                                {profileUser?.handle} <span style={{ fontSize: '0.8rem' }}>🚀</span>
                            </span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '5px' }}>
                            <div style={{ display: 'flex', gap: '15px', textAlign: 'center' }}>
                                <div><div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>{profileUser?.post_count ?? profilePosts.length}</div><div style={{ fontSize: "0.75rem", color: "#aaa" }}>Posts</div></div>
                                <div onClick={handleOpenFriendsModal} style={{ cursor: 'pointer' }}><div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>{(profileUser?.friends || []).length}</div><div style={{ fontSize: '0.75rem', color: '#aaa' }}>Friends</div></div>
                            </div>
                            {user && !isOwnProfile && (
                                <button onClick={handleFollowToggle} style={{ marginTop: '5px', background: isFollowing ? 'transparent' : '#2ed573', border: isFollowing ? '1px solid rgba(255,255,255,0.5)' : 'none', color: 'white', padding: '5px 15px', borderRadius: '15px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 'bold' }}>{isFollowing ? 'Following' : 'Follow'}</button>
                            )}
                        </div>
                    </div>
                    <div style={{ textAlign: 'center', marginTop: '20px' }}>
                        <p style={{ lineHeight: '1.5', color: '#ddd', maxWidth: '600px', margin: '0 auto' }}>{profileUser?.bio || "No bio yet."}</p>
                        {profileUser?.social_links?.website && (
                            user ? (
                                <a href={profileUser.social_links.website} target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginTop: '10px', color: '#1e90ff', textDecoration: 'none' }}>🔗 {profileUser.social_links.website}</a>
                            ) : (
                                <span onClick={() => onTriggerLogin && onTriggerLogin()} style={{ display: 'inline-block', marginTop: '10px', color: '#1e90ff', textDecoration: 'none', cursor: 'pointer' }}>🔗 {profileUser.social_links.website}</span>
                            )
                        )}
                        <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
                            {user && !isOwnProfile && (
                                <button onClick={handleMessageClick} style={{ background: isNexus ? 'linear-gradient(45deg, #a55eea, #45aaf2)' : '#1e90ff', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '20px', cursor: 'pointer', fontWeight: 'bold', display:'flex', alignItems:'center', gap:'8px', boxShadow: isNexus ? '0 0 15px rgba(165, 94, 234, 0.5)' : 'none' }}>
                                    {isNexus ? (<span>✨ Chat with AI</span>) : ('Message')}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
                <div style={{ display: 'flex', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                    {visibleTabs.map(tab => (
                        <button key={tab} onClick={() => setActiveTab(tab)} style={{ flex: 1, padding: '20px', background: activeTab === tab ? 'rgba(255,255,255,0.05)' : 'transparent', border: 'none', color: activeTab === tab ? (tab === 'gallery' ? '#1e90ff' : '#2ed573') : '#aaa', cursor: 'pointer', fontWeight: 'bold', textTransform: 'uppercase' }}>{tab}</button>
                    ))}
                </div>
            </div>
            <div style={{ marginTop: '20px' }}>
                {activeTab === 'posts' && (
                    <Feed user={user} posts={profilePosts} hasMore={profilePostsHasMore} onLoadMore={loadMoreProfilePosts} loadingMore={profilePostsLoading} onAddPost={() => {}} onDeletePost={handleLocalDeletePost} onLikePost={handleLocalLikePost} onAddComment={handleLocalAddComment} onVoteComment={handleLocalVoteComment} onDeleteComment={handleLocalDeleteComment} onEditComment={handleLocalEditComment} onEditPost={handleLocalEditPost} onNavigateProfile={handleProfileNavigation} onNavigatePost={onNavigatePost} onSearchTag={onSearchTag} showInput={false} onTriggerLogin={onTriggerLogin} />
                )}
                {activeTab === 'gallery' && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px', position: 'relative' }}>
                        {galleryImages.length > 0 ? galleryImages.map((p, index) => (
                            <div key={p.id} onClick={() => setSelectedPhotoIndex(index)} style={{ aspectRatio: '1/1', borderRadius: '10px', overflow: 'hidden', cursor: 'pointer', border: '1px solid rgba(255,255,255,0.1)' }}>
                                <img src={p.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                            </div>
                        )) : <div style={{ color: '#aaa', padding: '20px' }}>No photos found.</div>}
                    </div>
                )}
                {activeTab === 'about' && (
                    <div className="card" style={{ padding: '30px', color: '#ccc', lineHeight: '1.6' }}>
                        <h3>About {profileUser?.name}</h3>
                        <p>Joined: {formatJoinDate(profileUser?.joined_at)}</p>
                        <p>Reputation: {profilePosts.reduce((acc, p) => acc + (p.likes || 0), 0)} Likes</p>
                    </div>
                )}
            </div>
            {selectedPhotoIndex !== null && (
                <div onClick={() => setSelectedPhotoIndex(null)} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.95)', zIndex: 10000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <button onClick={prevPhoto} style={{ position: 'absolute', left: '20px', background: 'none', border: 'none', color: 'white', fontSize: '40px', cursor: 'pointer', padding: '20px', zIndex: 10001 }}>❮</button>
                    <img src={galleryImages[selectedPhotoIndex].url} style={{ maxWidth: '90%', maxHeight: '90%', borderRadius: '10px', boxShadow: '0 0 50px rgba(0,0,0,0.5)' }} alt="Fullscreen" onClick={(e) => e.stopPropagation()} />
                    <button onClick={nextPhoto} style={{ position: 'absolute', right: '20px', background: 'none', border: 'none', color: 'white', fontSize: '40px', cursor: 'pointer', padding: '20px', zIndex: 10001 }}>❯</button>
                </div>
            )}
            <style>{`
                .profile-info { padding: 0 30px 30px 30px; }
                .profile-avatar { width: 120px; height: 120px; }
                @media (max-width: 600px) {
                    .profile-info { padding: 0 10px 20px 10px !important; }
                    .profile-avatar { width: 100px !important; height: 100px !important; top: -50px !important; }
                }
            `}</style>
        </div>
    );
}

export default Profile;
